// Sprint M5.56b (14.08.2026) — Post-Purchase Welcome-Seite fuer Ablefy.
//
// Ablefy leitet nach dem Kauf per Success-URL auf eine der drei Routen:
//   /flex4success/welcome/pro-solo-monthly
//   /flex4success/welcome/pro-solo-annual
//   /flex4success/welcome/power-paket
// und haengt die vier ausgewaehlten Parameter automatisch als Query an:
//   ?order_token=...&payer_email=...&product=...&recurring_form=...
//
// Falls einer der vier Params fehlt / leer ist ODER der Backend-Lookup
// keinen User findet, zeigen wir das Fallback: E-Mail-Eingabefeld +
// Magic-Link-Button.

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Copy, Loader2, Mail, Sparkles, ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/config/brand";

/** Plan-spezifische Copy — matcht Andreas' M5.56b-Spec. */
const PLAN_COPY = {
  "pro-solo-monthly": {
    title: "Willkommen bei Pro Solo",
    subTitle: "Monatsabo",
    subHeadline:
      "30 Tage, in denen du lernst, wie du wirkst — statt nur was du sagst.",
    accent: "from-blue-500 to-cyan-500",
  },
  "pro-solo-annual": {
    title: "Willkommen bei Pro Solo",
    subTitle: "Jahresabo",
    subHeadline: "365 Tage bewusste Wirkung.",
    accent: "from-indigo-500 to-purple-500",
  },
  "power-paket": {
    title: "Willkommen zum PowerPaket",
    subTitle: "Dauerhafter Zugang",
    subHeadline:
      "Dein Bonus PowerPaket dauerhaft — weil Wirkungskompetenz deine grösste Investition ist.",
    accent: "from-amber-500 to-orange-500",
  },
};

function CopyableCode({ code }) {
  const [copied, setCopied] = useState(false);
  if (!code) return null;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — silent */
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-neutral-700 bg-neutral-900/60 px-4 py-3 font-mono text-base tracking-wide sm:text-lg">
      <span data-testid="license-code-value" className="min-w-0 flex-1 break-all text-white">
        {code}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        data-testid="license-code-copy-btn"
        aria-label="Lizenzschluessel kopieren"
        className="inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-white/10 px-3 py-1.5 text-sm text-white transition hover:bg-white/20"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied ? "Kopiert" : "Kopieren"}
      </button>
    </div>
  );
}

function Fallback({ initialEmail, onSuccess }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !email.includes("@")) {
      setErr("Bitte gib eine gueltige E-Mail-Adresse ein.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/welcome/magic-link", { email });
      setSent(true);
      onSuccess?.();
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "Bitte erneut versuchen.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="welcome-fallback-sent"
        className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-emerald-100"
      >
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Check dein Postfach.</p>
            <p className="mt-1 text-sm opacity-90">
              Wenn eine Bestellung zu dieser E-Mail existiert, findest du in Kuerze
              einen Login-Link. Von dort geht&apos;s direkt in deinen PocketCoach.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="welcome-fallback-form"
      className="rounded-2xl border border-neutral-700 bg-neutral-900/40 p-6"
    >
      <p className="mb-1 text-sm font-semibold text-white">
        Kleiner Zwischenschritt
      </p>
      <p className="mb-4 text-sm text-neutral-300">
        Wir konnten deine Bestellung nicht automatisch verknuepfen. Gib deine
        Kauf-E-Mail ein — wir schicken dir einen Login-Link.
      </p>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="deine@email.de"
        data-testid="welcome-fallback-email-input"
        className="mb-3 w-full rounded-lg border border-neutral-600 bg-neutral-900 px-4 py-3 text-white placeholder-neutral-500 focus:border-blue-500 focus:outline-none"
        required
      />
      {err && (
        <p data-testid="welcome-fallback-error" className="mb-3 text-sm text-red-400">
          {err}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        data-testid="welcome-fallback-submit-btn"
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-3 font-semibold text-neutral-900 transition hover:bg-neutral-200 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
        Magic-Link anfordern
      </button>
    </form>
  );
}

export default function WelcomePlan({ plan }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Sprint M5.56b Bug 1 Fix (14.08.2026):
  // URLSearchParams.get() dekodiert `+` als Space (Form-Encoding-Konvention).
  // Ablefy haengt `payer_email=andreas+test@laimisch.de` unencoded an — nach
  // dem Parse steht dort `andreas test@laimisch.de`. Wir stellen das `+`
  // wieder her, damit der Backend-Lookup (find_user_by_email) matched.
  const restorePlus = (v) => (v || "").replace(/ /g, "+");
  const orderToken = params.get("order_token") || "";
  const payerEmail = restorePlus(params.get("payer_email"));
  const productParam = params.get("product") || "";
  const recurringForm = params.get("recurring_form") || "";

  // "Defensive Spec": alle 4 Params muessen vorhanden sein, sonst Fallback.
  const hasAllParams = useMemo(
    () => Boolean(orderToken && payerEmail && productParam && recurringForm),
    [orderToken, payerEmail, productParam, recurringForm]
  );

  const [phase, setPhase] = useState(hasAllParams ? "loading" : "fallback"); // loading | success | fallback | rate_limited
  const [data, setData] = useState(null);
  const [ctaState, setCtaState] = useState("idle"); // idle | sending | sent
  const [ctaError, setCtaError] = useState("");
  const copy = PLAN_COPY[plan] || PLAN_COPY["pro-solo-monthly"];

  useEffect(() => {
    document.title = `${copy.title} — ${BRAND.name}`;
  }, [copy.title]);

  useEffect(() => {
    if (!hasAllParams) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/welcome-data", {
          params: {
            order_token: orderToken,
            payer_email: payerEmail,
            product: productParam,
            recurring_form: recurringForm,
          },
        });
        if (cancelled) return;
        // Sprint M5.56b Bug 3 Fix (14.08.2026):
        // Backend liefert 200 auch wenn der User keine (aktive) Lizenz hat
        // (webhook noch nicht durchgelaufen). Kein license_tier -> Fallback,
        // damit der Kaeufer per Magic-Link seinen Zugang triggern kann.
        if (!resp.data?.license_tier) {
          setData(resp.data);
          setPhase("fallback");
          return;
        }
        setData(resp.data);
        setPhase("success");
      } catch (ex) {
        if (cancelled) return;
        // 429 -> gesonderte Anzeige (kurz warten), sonst Fallback
        if (ex?.response?.status === 429) {
          setPhase("rate_limited");
        } else {
          setPhase("fallback");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasAllParams, orderToken, payerEmail, productParam, recurringForm]);

  const isAuthenticated = user && typeof user === "object";

  const handleCockpitCta = async () => {
    if (isAuthenticated) {
      navigate("/cockpit");
      return;
    }
    // Nicht eingeloggt: Kaufer kommt frisch von Ablefy und hat keine Session.
    // Anstatt auf /login zu navigieren, triggern wir automatisch den Magic-Link
    // an die im Backend gefundene Nutzer-Email (data.user_email = Login-Email
    // auch dann korrekt, wenn Ablefy eine Wallet-Email geliefert hat).
    const targetEmail = data?.user_email || payerEmail;
    if (!targetEmail) {
      navigate("/login?next=/cockpit");
      return;
    }
    setCtaState("sending");
    setCtaError("");
    try {
      await api.post("/welcome/magic-link", { email: targetEmail });
      setCtaState("sent");
    } catch (ex) {
      setCtaState("idle");
      setCtaError(
        formatApiErrorDetail(ex?.response?.data?.detail) ||
          "Bitte erneut versuchen."
      );
    }
  };

  return (
    <div
      data-testid={`welcome-page-${plan}`}
      className="min-h-screen bg-neutral-950 px-4 py-10 text-white sm:py-16"
    >
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8 text-center sm:mb-12">
          <div
            className={`mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br ${copy.accent} shadow-lg`}
          >
            <CheckCircle2 className="h-8 w-8 text-white" />
          </div>
          <h1
            data-testid="welcome-title"
            className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl"
          >
            {copy.title}
          </h1>
          <p className="mt-2 text-base text-neutral-400 sm:text-lg">
            {copy.subTitle}
          </p>
          <p
            data-testid="welcome-subheadline"
            className="mt-6 text-base leading-relaxed text-neutral-200 sm:text-lg"
          >
            {copy.subHeadline}
          </p>
        </div>

        {/* Loading */}
        {phase === "loading" && (
          <div
            data-testid="welcome-loading"
            className="flex items-center justify-center gap-3 rounded-2xl border border-neutral-800 bg-neutral-900/40 p-8 text-neutral-300"
          >
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Wir verknuepfen deine Bestellung...</span>
          </div>
        )}

        {/* Success */}
        {phase === "success" && data && (
          <>
            {/* Lizenzschluessel */}
            <div className="mb-6 rounded-2xl border border-neutral-800 bg-neutral-900/40 p-6">
              <p className="mb-3 text-sm uppercase tracking-wider text-neutral-400">
                Dein Lizenzschluessel
              </p>
              {data.license_code ? (
                <CopyableCode code={data.license_code} />
              ) : (
                <p data-testid="license-code-missing" className="text-neutral-300">
                  Dein Lizenzschluessel wird gerade angelegt. Du findest ihn
                  gleich in deinem Mitgliederbereich.
                </p>
              )}
              {data.user_email && (
                <p className="mt-3 text-xs text-neutral-500">
                  Verknuepft mit: <span className="text-neutral-300">{data.user_email}</span>
                </p>
              )}
            </div>

            {/* PowerPaket-Bonus-Badge — NUR fuer Jahresplan */}
            {plan === "pro-solo-annual" && data.powerpaket_lifetime && (
              <div
                data-testid="powerpaket-bonus-block"
                className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6"
              >
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-300" />
                  <span className="text-sm font-semibold uppercase tracking-wider text-amber-200">
                    Bonus freigeschaltet
                  </span>
                </div>
                <p className="mb-4 text-base font-semibold text-white">
                  PowerPaket Lifetime freigeschaltet
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/powerpaket")}
                  data-testid="powerpaket-cta-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-amber-400/40 bg-amber-500/20 px-4 py-2 text-sm font-semibold text-amber-100 transition hover:bg-amber-500/30"
                >
                  PowerPaket-Module ansehen
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Primaer-CTA — genau EIN Button */}
            {ctaState === "sent" ? (
              <div
                data-testid="welcome-cta-sent"
                className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-emerald-100"
              >
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Check dein Postfach.</p>
                    <p className="mt-1 text-sm opacity-90">
                      Wir haben dir einen Login-Link an{" "}
                      <span className="font-mono">{data?.user_email || payerEmail}</span>{" "}
                      geschickt. Klick darauf und du landest direkt in deinem PocketCoach.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCockpitCta}
                  disabled={ctaState === "sending"}
                  data-testid="welcome-cockpit-cta"
                  className={`group inline-flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r ${copy.accent} px-6 py-4 text-lg font-semibold text-white shadow-lg transition hover:shadow-xl disabled:opacity-70`}
                >
                  {ctaState === "sending" ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Meinen PocketCoach öffnen
                      <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                {ctaError && (
                  <p
                    data-testid="welcome-cta-error"
                    className="mt-3 text-center text-sm text-red-400"
                  >
                    {ctaError}
                  </p>
                )}
              </>
            )}

            {/* Sekundaerer Text (kein Button) */}
            <p
              data-testid="welcome-invoice-note"
              className="mt-6 text-center text-sm leading-relaxed text-neutral-400"
            >
              Rechnung &amp; Abo-Verwaltung findest du in deiner
              Ablefy-Kaufbestaetigungs-Mail.
            </p>
          </>
        )}

        {/* Rate-Limited — Kaeufer sieht seine Bestellung nicht, weil zu viele Requests
            in kurzer Zeit. Hinweis + Retry-Anleitung. */}
        {phase === "rate_limited" && (
          <div
            data-testid="welcome-rate-limited"
            className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 text-amber-100"
          >
            <p className="font-semibold">Kurzer Moment.</p>
            <p className="mt-2 text-sm opacity-90">
              Der Server hat gerade viele Anfragen. Bitte lade diese Seite in
              1-2 Minuten neu — dann steht deine Lizenz bereit.
            </p>
          </div>
        )}

        {/* Fallback */}
        {phase === "fallback" && (
          <Fallback initialEmail={payerEmail} />
        )}
      </div>
    </div>
  );
}
